"use client";

import { useEffect, useState } from "react";

type Summary = {
  ok: boolean;
  today?: { cost: number; totalTokens: number; messageCount: number };
  last7?: number;
  last30?: number;
};

const fmtUsd = (v: number) => `$${v >= 10 ? v.toFixed(2) : v.toFixed(4)}`;
const fmtTokens = (v: number) => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}m`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}k`;
  return `${Math.round(v)}`;
};

export default function CostSidebarWidget() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [range, setRange] = useState<"1D" | "7D" | "30D">("1D");

  useEffect(() => {
    let cancelled = false;
    fetch("/api/costs/summary", { cache: "no-store" })
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setSummary(d); })
      .catch(() => { if (!cancelled) setSummary({ ok: false }); });
    return () => { cancelled = true; };
  }, []);

  const cost = range === "1D" ? summary?.today?.cost : range === "7D" ? summary?.last7 : summary?.last30;
  const tokens = summary?.today?.totalTokens ?? 0;

  function openCosts() {
    // Force a document navigation instead of Next client-side routing.
    // In dev mode, Next can keep a stale route payload in memory; this avoids
    // the tracker reopening an old /costs module after a hot reload.
    window.location.href = "/costs";
  }

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={openCosts}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") openCosts();
      }}
      className="block rounded-xl bg-white/[0.06] px-3.5 py-3 hover:bg-white/10 transition-colors cursor-pointer ring-1 ring-white/5"
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider text-slate-400">API Cost</span>
        <span className="text-xs font-semibold text-white">{summary ? fmtUsd(cost ?? 0) : "…"}</span>
      </div>
      <div className="text-[10px] text-slate-500 mb-2">
        {summary?.today ? `${fmtTokens(tokens)} tokens · Today` : "Retroactive token scan"}
      </div>
      <div className="flex gap-1 text-[10px]">
        {(["1D", "7D", "30D"] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={(e) => { e.stopPropagation(); setRange(p); }}
            className={[
              "flex-1 py-1 rounded-md transition-colors",
              range === p ? "bg-sl-gold/20 text-white" : "bg-white/5 text-slate-400 hover:text-white",
            ].join(" ")}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}
